
import React, { useState } from 'react';
import { 
  BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell, AreaChart, Area, YAxis, PieChart, Pie
} from 'recharts';
import { 
  Calendar as CalendarIcon, 
  Home, 
  Users, 
  Settings, 
  Bell, 
  MessageCircle, 
  Clock, 
  MoreVertical,
  Plus,
  DollarSign,
  Scissors,
  LogOut,
  RefreshCw,
  Package,
  Briefcase,
  TrendingUp,
  Search,
  Filter,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { GlassCard, GlassButton, Avatar, SidebarItem, GlassInput } from '../components/GlassUI';
import { MOCK_MASTER, INITIAL_APPOINTMENTS, MOCK_CLIENTS, MOCK_SERVICES, MOCK_PRODUCTS, MOCK_EMPLOYEES } from '../constants';
import { Appointment, Product } from '../types';

interface MasterViewProps {
  onLogout: () => void;
}

const DAYS = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'];
const DATES = Array.from({ length: 7 }, (_, i) => 23 + i);

export const MasterView: React.FC<MasterViewProps> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState<'home' | 'calendar' | 'clients' | 'services' | 'finance' | 'inventory' | 'staff' | 'settings'>('home');
  const [appointments] = useState<Appointment[]>(INITIAL_APPOINTMENTS);
  const [selectedDateIndex, setSelectedDateIndex] = useState(3);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = () => {
    setIsSyncing(true);
    setTimeout(() => {
      setIsSyncing(false);
      alert('Синхронизация завершена!');
    }, 1500);
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'home': return <Dashboard appointments={appointments} />;
      case 'calendar': return <CalendarView appointments={appointments} selectedDateIndex={selectedDateIndex} onSelectDate={setSelectedDateIndex} onSync={handleSync} isSyncing={isSyncing} />;
      case 'clients': return <CRMView />;
      case 'services': return <ServicesView />;
      case 'inventory': return <InventoryView />;
      case 'staff': return <StaffView />;
      case 'finance': return <FinanceView />;
      case 'settings': return <SettingsView onLogout={onLogout} onSync={handleSync} />;
      default: return <Dashboard appointments={appointments} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      {/* --- Desktop Sidebar --- */}
      <aside className="hidden md:flex flex-col w-72 bg-white border-r border-slate-200 fixed h-full z-20">
        <div className="p-8 pb-4">
           <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-2xl shadow-xl shadow-indigo-100">G</div>
              <div>
                <span className="text-xl font-black text-slate-900 block leading-none">GlowBook</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 block">SaaS Platform</span>
              </div>
           </div>
        </div>

        <nav className="px-4 space-y-1 flex-1 overflow-y-auto no-scrollbar">
          <p className="px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 mt-4">Основное</p>
          <SidebarItem icon={<Home size={20} />} label="Дашборд" active={activeTab === 'home'} onClick={() => setActiveTab('home')} />
          <SidebarItem icon={<CalendarIcon size={20} />} label="Журнал записи" active={activeTab === 'calendar'} onClick={() => setActiveTab('calendar')} />
          <SidebarItem icon={<Users size={20} />} label="Клиентская база" active={activeTab === 'clients'} onClick={() => setActiveTab('clients')} />
          
          <p className="px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 mt-6">Бизнес</p>
          <SidebarItem icon={<Scissors size={20} />} label="Услуги и Прайс" active={activeTab === 'services'} onClick={() => setActiveTab('services')} />
          <SidebarItem icon={<Package size={20} />} label="Склад и Товары" active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')} />
          <SidebarItem icon={<Briefcase size={20} />} label="Персонал" active={activeTab === 'staff'} onClick={() => setActiveTab('staff')} />
          <SidebarItem icon={<DollarSign size={20} />} label="Финансы" active={activeTab === 'finance'} onClick={() => setActiveTab('finance')} />
        </nav>

        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
          <SidebarItem icon={<Settings size={20} />} label="Настройки" active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-all mt-1">
            <LogOut size={20} />
            <span className="font-semibold">Выйти</span>
          </button>
        </div>
      </aside>

      {/* --- Mobile Bottom Nav --- */}
      <div className="md:hidden fixed bottom-4 left-4 right-4 z-50">
        <div className="bg-white/95 backdrop-blur-md border border-slate-200 shadow-2xl rounded-2xl flex justify-between items-center p-2 px-4">
          <NavButton icon={<Home size={22} />} active={activeTab === 'home'} onClick={() => setActiveTab('home')} />
          <NavButton icon={<CalendarIcon size={22} />} active={activeTab === 'calendar'} onClick={() => setActiveTab('calendar')} />
          <NavButton icon={<Plus size={28} className="text-white" />} special onClick={() => setActiveTab('calendar')} active={false} />
          <NavButton icon={<Users size={22} />} active={activeTab === 'clients'} onClick={() => setActiveTab('clients')} />
          <NavButton icon={<Package size={22} />} active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')} />
        </div>
      </div>

      {/* --- Main Area --- */}
      <main className="flex-1 md:ml-72 min-h-screen">
        <header className="hidden md:flex sticky top-0 bg-white/80 backdrop-blur-md z-10 border-b border-slate-200 h-20 items-center justify-between px-10">
           <div className="flex items-center gap-4 bg-slate-100 px-4 py-2 rounded-xl w-96">
              <Search size={18} className="text-slate-400" />
              <input type="text" placeholder="Поиск по клиентам или записям..." className="bg-transparent border-none outline-none text-sm text-slate-700 w-full" />
           </div>
           
           <div className="flex items-center gap-6">
              <button className="relative p-2 rounded-full hover:bg-slate-100 text-slate-500 transition-all">
                <Bell size={20} />
                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
              </button>
              <div className="h-8 w-px bg-slate-200"></div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900 leading-none">{MOCK_MASTER.name}</p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Владелец бизнеса</p>
                </div>
                <Avatar src={MOCK_MASTER.avatar} alt={MOCK_MASTER.name} size="md" className="border-2 border-indigo-100" />
              </div>
           </div>
        </header>

        <div className="p-6 md:p-10 max-w-[1600px] mx-auto pb-32 md:pb-10">
          <div className="md:hidden flex justify-between items-center mb-8">
             <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">G</div>
                <h1 className="text-xl font-bold text-slate-900">GlowBook</h1>
             </div>
             <Avatar src={MOCK_MASTER.avatar} alt={MOCK_MASTER.name} size="sm" />
          </div>
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

const NavButton: React.FC<{ icon: React.ReactNode; active: boolean; onClick: () => void; special?: boolean }> = ({ icon, active, onClick, special }) => (
  <button 
    onClick={onClick}
    className={`p-3 rounded-xl transition-all duration-300 flex items-center justify-center
      ${special 
        ? 'bg-indigo-600 shadow-xl shadow-indigo-200 -mt-10 w-14 h-14 rounded-full border-4 border-white' 
        : active ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400'
      }`}
  >
    {icon}
  </button>
);

// --- DASHBOARD ---
const Dashboard: React.FC<{ appointments: Appointment[] }> = ({ appointments }) => {
  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <GlassCard className="bg-white border-slate-200 flex flex-col justify-between">
           <div className="flex justify-between">
             <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl"><Users size={24}/></div>
             <span className="text-xs font-bold text-green-500 flex items-center gap-1"><TrendingUp size={12}/> +12%</span>
           </div>
           <div className="mt-4">
             <h3 className="text-2xl font-black text-slate-900">1,240</h3>
             <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mt-1">Клиентов в базе</p>
           </div>
        </GlassCard>
        
        <GlassCard className="bg-white border-slate-200 flex flex-col justify-between">
           <div className="flex justify-between">
             <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl"><DollarSign size={24}/></div>
             <span className="text-xs font-bold text-green-500 flex items-center gap-1"><TrendingUp size={12}/> +8.4%</span>
           </div>
           <div className="mt-4">
             <h3 className="text-2xl font-black text-slate-900">42,500 ₽</h3>
             <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mt-1">Выручка сегодня</p>
           </div>
        </GlassCard>

        <GlassCard className="bg-white border-slate-200 flex flex-col justify-between">
           <div className="flex justify-between">
             <div className="p-3 bg-amber-50 text-amber-600 rounded-xl"><CalendarIcon size={24}/></div>
             <span className="text-xs font-bold text-slate-400">В ожидании</span>
           </div>
           <div className="mt-4">
             <h3 className="text-2xl font-black text-slate-900">14</h3>
             <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mt-1">Записей на сегодня</p>
           </div>
        </GlassCard>

        <GlassCard className="bg-white border-slate-200 flex flex-col justify-between">
           <div className="flex justify-between">
             <div className="p-3 bg-rose-50 text-rose-600 rounded-xl"><AlertCircle size={24}/></div>
             <span className="text-xs font-bold text-rose-500">-2.1%</span>
           </div>
           <div className="mt-4">
             <h3 className="text-2xl font-black text-slate-900">12%</h3>
             <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mt-1">Процент отмен</p>
           </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <GlassCard className="lg:col-span-2 p-8">
          <div className="flex items-center justify-between mb-8">
             <div>
                <h2 className="text-lg font-bold text-slate-900">Статистика доходов</h2>
                <p className="text-xs text-slate-400 font-medium">Динамика за последние 7 дней</p>
             </div>
             <select className="bg-slate-50 border border-slate-200 text-xs font-bold px-3 py-2 rounded-lg outline-none cursor-pointer">
                <option>Эта неделя</option>
                <option>Прошлый месяц</option>
             </select>
          </div>
          <div className="h-72 w-full">
             <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={[
                  { name: 'ПН', val: 32000 }, { name: 'ВТ', val: 45000 }, { name: 'СР', val: 38000 }, 
                  { name: 'ЧТ', val: 52000 }, { name: 'ПТ', val: 68000 }, { name: 'СБ', val: 72000 }, { name: 'ВС', val: 55000 }
                ]}>
                  <defs>
                    <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#4f46e5" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 600}} dy={10} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="val" stroke="#4f46e5" strokeWidth={3} fillOpacity={1} fill="url(#colorVal)" />
                </AreaChart>
             </ResponsiveContainer>
          </div>
        </GlassCard>

        <GlassCard className="p-8">
           <h2 className="text-lg font-bold text-slate-900 mb-6">Статус записей</h2>
           <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                 <PieChart>
                    <Pie data={[
                      { name: 'Успешно', value: 85, fill: '#10b981' },
                      { name: 'Отмены', value: 10, fill: '#f43f5e' },
                      { name: 'Неявка', value: 5, fill: '#f59e0b' }
                    ]} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" />
                    <Tooltip />
                 </PieChart>
              </ResponsiveContainer>
           </div>
           <div className="space-y-3 mt-4">
              <div className="flex items-center justify-between text-xs font-bold">
                 <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500"></div> <span className="text-slate-500">Завершено</span></div>
                 <span className="text-slate-900">85%</span>
              </div>
              <div className="flex items-center justify-between text-xs font-bold">
                 <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-rose-500"></div> <span className="text-slate-500">Отмены</span></div>
                 <span className="text-slate-900">10%</span>
              </div>
              <div className="flex items-center justify-between text-xs font-bold">
                 <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-amber-500"></div> <span className="text-slate-500">Неявки</span></div>
                 <span className="text-slate-900">5%</span>
              </div>
           </div>
        </GlassCard>
      </div>
    </div>
  );
};

// --- CALENDAR ---
const CalendarView: React.FC<{ 
  appointments: Appointment[], 
  selectedDateIndex: number, 
  onSelectDate: (i: number) => void,
  onSync: () => void,
  isSyncing: boolean
}> = ({ appointments, selectedDateIndex, onSelectDate, onSync, isSyncing }) => {
  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
        <div>
           <h1 className="text-2xl font-black text-slate-900">Журнал записи</h1>
           <p className="text-sm text-slate-500 font-medium">Управление расписанием и сотрудниками</p>
        </div>
        <div className="flex gap-3">
           <button onClick={onSync} className="bg-white border border-slate-200 px-4 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2">
             <RefreshCw size={18} className={isSyncing ? "animate-spin" : ""} /> Обновить
           </button>
           <button className="bg-indigo-600 px-6 py-2 rounded-xl text-sm font-bold text-white shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2">
             <Plus size={18} /> Новая запись
           </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
        <div className="flex border-b border-slate-100">
          <div className="w-20 p-4 border-r border-slate-100 bg-slate-50 flex items-center justify-center font-black text-slate-300">GMT+3</div>
          <div className="flex-1 overflow-x-auto flex no-scrollbar">
             {DATES.map((date, idx) => (
               <div 
                key={idx} 
                onClick={() => onSelectDate(idx)}
                className={`min-w-[120px] flex-1 p-4 text-center cursor-pointer transition-all border-r border-slate-100 last:border-r-0 ${selectedDateIndex === idx ? 'bg-indigo-50 border-b-2 border-b-indigo-600' : 'hover:bg-slate-50'}`}
               >
                 <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${selectedDateIndex === idx ? 'text-indigo-600' : 'text-slate-400'}`}>{DAYS[idx]}</p>
                 <p className={`text-xl font-black ${selectedDateIndex === idx ? 'text-indigo-700' : 'text-slate-800'}`}>{date}</p>
               </div>
             ))}
          </div>
        </div>

        <div className="relative min-h-[600px] bg-slate-50/30">
           {/* Timeline Grid */}
           {Array.from({length: 12}).map((_, i) => (
             <div key={i} className="flex h-20 border-b border-slate-100">
               <div className="w-20 border-r border-slate-100 flex items-start justify-center pt-2 text-[10px] font-black text-slate-400">{9 + i}:00</div>
               <div className="flex-1 relative group">
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-indigo-50/50 cursor-pointer transition-all flex items-center justify-center">
                    <span className="text-indigo-600 font-bold text-xs">+ Записать на {9 + i}:00</span>
                  </div>
               </div>
             </div>
           ))}

           {/* Appointment Cards */}
           <div className="absolute top-[80px] left-[100px] right-4 bg-white border border-indigo-200 shadow-xl rounded-2xl p-4 flex items-center gap-4 w-[280px] z-10 hover:scale-105 transition-transform cursor-pointer border-l-8 border-l-indigo-600">
              <Avatar src={INITIAL_APPOINTMENTS[0].clientAvatar} alt="Client" size="md" />
              <div>
                <p className="font-black text-slate-900 text-sm leading-none">{INITIAL_APPOINTMENTS[0].clientName}</p>
                <p className="text-indigo-600 text-[10px] font-bold uppercase mt-1">{INITIAL_APPOINTMENTS[0].serviceName}</p>
                <div className="flex items-center gap-2 mt-2 text-slate-400 text-[10px] font-bold">
                   <Clock size={10} /> 10:00 - 11:00
                </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

// --- INVENTORY VIEW ---
const InventoryView: React.FC = () => {
  return (
    <div className="animate-in fade-in zoom-in-95 duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
        <div>
           <h1 className="text-2xl font-black text-slate-900">Склад и Товары</h1>
           <p className="text-sm text-slate-500 font-medium">Учет расходников и продажи товаров</p>
        </div>
        <button className="bg-indigo-600 px-6 py-2 rounded-xl text-sm font-bold text-white shadow-lg shadow-indigo-100 flex items-center gap-2">
           <Plus size={18} /> Добавить товар
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <GlassCard className="bg-amber-50 border-amber-100">
           <div className="flex items-center gap-3 text-amber-600 mb-2">
              <AlertCircle size={20} /> <span className="font-bold text-xs uppercase">Критический остаток</span>
           </div>
           <h3 className="text-xl font-black text-slate-900">4 товара</h3>
           <p className="text-slate-500 text-xs font-medium">Требуется пополнение запасов</p>
        </GlassCard>
        <GlassCard className="bg-indigo-50 border-indigo-100">
           <div className="flex items-center gap-3 text-indigo-600 mb-2">
              <Package size={20} /> <span className="font-bold text-xs uppercase">Всего позиций</span>
           </div>
           <h3 className="text-xl font-black text-slate-900">128 SKU</h3>
           <p className="text-slate-500 text-xs font-medium">На общую сумму 142 000 ₽</p>
        </GlassCard>
        <GlassCard className="bg-emerald-50 border-emerald-100">
           <div className="flex items-center gap-3 text-emerald-600 mb-2">
              <TrendingUp size={20} /> <span className="font-bold text-xs uppercase">Оборачиваемость</span>
           </div>
           <h3 className="text-xl font-black text-slate-900">1.4 мес</h3>
           <p className="text-slate-500 text-xs font-medium">Средний срок реализации товара</p>
        </GlassCard>
      </div>

      <GlassCard className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-left">
           <thead>
             <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Наименование</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Категория</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">На складе</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Цена</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Статус</th>
             </tr>
           </thead>
           <tbody className="divide-y divide-slate-100">
             {MOCK_PRODUCTS.map(product => (
               <tr key={product.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-slate-900">{product.name}</p>
                    <p className="text-[10px] text-slate-400 font-bold uppercase">ID: {product.id}</p>
                  </td>
                  <td className="px-6 py-4">
                     <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-[10px] font-bold uppercase">{product.category}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <p className="font-black text-slate-900">{product.stock} {product.unit}</p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <p className="font-black text-slate-900">{product.price} ₽</p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {product.stock <= product.minStock ? (
                      <span className="inline-flex items-center gap-1 text-rose-600 font-bold text-[10px] uppercase">
                        <AlertCircle size={14}/> Низкий запас
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-[10px] uppercase">
                        <CheckCircle2 size={14}/> В норме
                      </span>
                    )}
                  </td>
               </tr>
             ))}
           </tbody>
        </table>
      </GlassCard>
    </div>
  );
};

// --- STAFF VIEW ---
const StaffView: React.FC = () => {
  return (
    <div className="animate-in fade-in slide-in-from-left-4 duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
        <div>
           <h1 className="text-2xl font-black text-slate-900">Команда и Персонал</h1>
           <p className="text-sm text-slate-500 font-medium">Управление графиком и зарплатами</p>
        </div>
        <button className="bg-indigo-600 px-6 py-2 rounded-xl text-sm font-bold text-white shadow-lg shadow-indigo-100 flex items-center gap-2">
           <Plus size={18} /> Добавить мастера
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {MOCK_EMPLOYEES.map(emp => (
          <GlassCard key={emp.id} className="p-6 hover:shadow-xl transition-all border-slate-200">
             <div className="flex items-center gap-4 mb-6">
                <Avatar src={emp.avatar} alt={emp.name} size="xl" />
                <div>
                   <h3 className="text-lg font-black text-slate-900">{emp.name}</h3>
                   <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider">{emp.position}</p>
                   <div className="flex items-center gap-1 text-amber-500 text-xs mt-1 font-black">
                     <StarIcon fill="currentColor" size={12}/> {emp.rating}
                   </div>
                </div>
             </div>
             
             <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="flex justify-between items-center">
                   <span className="text-[10px] font-black text-slate-400 uppercase">Смена сегодня</span>
                   <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded uppercase">09:00 - 18:00</span>
                </div>
                <div className="flex justify-between items-center">
                   <span className="text-[10px] font-black text-slate-400 uppercase">Выручка за мес.</span>
                   <span className="text-sm font-black text-slate-800">124 500 ₽</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                   <button className="text-[10px] font-black uppercase text-slate-600 bg-slate-100 py-2 rounded-lg hover:bg-slate-200 transition-colors">График</button>
                   <button className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 py-2 rounded-lg hover:bg-indigo-100 transition-colors">Профиль</button>
                </div>
             </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
};

// --- FINANCE VIEW ---
const FinanceView: React.FC = () => {
    return (
        <div className="space-y-8 animate-in fade-in duration-500">
             <div className="flex flex-col md:flex-row justify-between md:items-center gap-6">
                <div>
                    <h1 className="text-2xl font-black text-slate-900">Финансовая аналитика</h1>
                    <p className="text-sm text-slate-500 font-medium">Денежные потоки, доходы и расходы</p>
                </div>
                <div className="flex gap-2">
                   <button className="bg-white border border-slate-200 px-4 py-2 rounded-xl text-xs font-bold text-slate-600">CSV Экспорт</button>
                   <button className="bg-emerald-600 px-6 py-2 rounded-xl text-xs font-bold text-white shadow-lg shadow-emerald-100">+ Кассовая операция</button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <GlassCard className="bg-white border-slate-200">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Чистая прибыль</p>
                   <h2 className="text-2xl font-black text-emerald-600">324 000 ₽</h2>
                   <p className="text-[10px] font-bold text-emerald-500 mt-1">+14% к пр. месяцу</p>
                </GlassCard>
                <GlassCard className="bg-white border-slate-200">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Средний чек</p>
                   <h2 className="text-2xl font-black text-slate-900">2 450 ₽</h2>
                   <p className="text-[10px] font-bold text-slate-400 mt-1">На базе 540 визитов</p>
                </GlassCard>
                <GlassCard className="bg-white border-slate-200">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Расходы</p>
                   <h2 className="text-2xl font-black text-rose-500">86 400 ₽</h2>
                   <p className="text-[10px] font-bold text-rose-400 mt-1">Аренда, материалы, ЗП</p>
                </GlassCard>
                <GlassCard className="bg-indigo-600 border-indigo-700 text-white">
                   <p className="text-[10px] font-black text-indigo-200 uppercase tracking-widest mb-2">На балансе</p>
                   <h2 className="text-2xl font-black">450 000 ₽</h2>
                   <p className="text-[10px] font-bold text-indigo-300 mt-1">Свободные средства</p>
                </GlassCard>
            </div>

            <GlassCard className="p-8">
               <h3 className="font-black text-slate-900 mb-8">Структура расходов</h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                       <PieChart>
                          <Pie data={[
                            { name: 'Аренда', value: 40000, fill: '#6366f1' },
                            { name: 'Зарплаты', value: 120000, fill: '#10b981' },
                            { name: 'Материалы', value: 25000, fill: '#f59e0b' },
                            { name: 'Маркетинг', value: 15000, fill: '#ec4899' }
                          ]} cx="50%" cy="50%" innerRadius={60} outerRadius={80} dataKey="value" />
                          <Tooltip />
                       </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-4">
                     {['Зарплаты (60%)', 'Аренда (20%)', 'Материалы (12%)', 'Маркетинг (8%)'].map((item, idx) => (
                       <div key={idx} className="flex justify-between items-center pb-2 border-b border-slate-100">
                          <span className="text-xs font-bold text-slate-500">{item}</span>
                          <span className="h-2 w-2 rounded-full bg-slate-300"></span>
                       </div>
                     ))}
                  </div>
               </div>
            </GlassCard>
        </div>
    )
}

// Reuse other simple views from previous context but styled for new layout
const CRMView: React.FC = () => (
  <div className="animate-in fade-in duration-500">
    <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
        <div>
           <h1 className="text-2xl font-black text-slate-900">Клиентская база</h1>
           <p className="text-sm text-slate-500 font-medium">Управление профилями и историей визитов</p>
        </div>
        <div className="flex gap-2">
            <GlassInput placeholder="Поиск клиента..." icon={<Search size={16}/>} className="md:w-64" />
            <button className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600 transition-colors"><Filter size={20}/></button>
        </div>
    </div>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {MOCK_CLIENTS.map(client => (
        <GlassCard key={client.id} className="p-6 border-slate-200 hover:border-indigo-200 transition-all group flex items-center justify-between">
           <div className="flex items-center gap-4">
              <Avatar src={client.avatar} alt={client.name} size="xl" />
              <div>
                 <h3 className="text-lg font-black text-slate-900">{client.name}</h3>
                 <p className="text-xs font-bold text-slate-400 mt-1 uppercase">VIP КЛИЕНТ</p>
                 <div className="flex gap-4 mt-3">
                    <div>
                       <p className="text-[10px] font-black text-slate-300 uppercase">Визитов</p>
                       <p className="text-sm font-black text-slate-700">12</p>
                    </div>
                    <div>
                       <p className="text-[10px] font-black text-slate-300 uppercase">Оборот</p>
                       <p className="text-sm font-black text-indigo-600">24 800 ₽</p>
                    </div>
                 </div>
              </div>
           </div>
           <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button className="p-3 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors"><MessageCircle size={20}/></button>
              <button className="p-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors"><MoreVertical size={20}/></button>
           </div>
        </GlassCard>
      ))}
    </div>
  </div>
);

const ServicesView: React.FC = () => (
    <div className="animate-in fade-in duration-500">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
            <div>
               <h1 className="text-2xl font-black text-slate-900">Услуги и Прайс</h1>
               <p className="text-sm text-slate-500 font-medium">Список услуг и расходники</p>
            </div>
            <button className="bg-indigo-600 px-6 py-2 rounded-xl text-sm font-bold text-white shadow-lg shadow-indigo-100">+ Добавить услугу</button>
        </div>
        <div className="space-y-4">
            {MOCK_SERVICES.map(s => (
                <GlassCard key={s.id} className="p-4 border-slate-200 hover:border-indigo-100 transition-all flex items-center justify-between">
                   <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl ${s.color}`}>{s.name[0]}</div>
                      <div>
                         <h3 className="font-black text-slate-900">{s.name}</h3>
                         <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{s.category} • {s.durationMin} мин</p>
                      </div>
                   </div>
                   <div className="text-right">
                      <p className="text-xl font-black text-slate-900">{s.price} ₽</p>
                      <button className="text-[10px] font-black text-indigo-600 uppercase mt-1 hover:underline">Редактировать</button>
                   </div>
                </GlassCard>
            ))}
        </div>
    </div>
)

const SettingsView: React.FC<{ onLogout: () => void, onSync: () => void }> = ({ onLogout, onSync }) => (
    <div className="animate-in fade-in duration-500 max-w-2xl">
        <h1 className="text-2xl font-black text-slate-900 mb-8">Настройки системы</h1>
        <GlassCard className="p-8 space-y-8 border-slate-200">
            <div className="flex items-center gap-6 pb-8 border-b border-slate-100">
                <Avatar src={MOCK_MASTER.avatar} alt="Profile" size="xl" className="w-24 h-24" />
                <div>
                    <h3 className="text-xl font-black text-slate-900">{MOCK_MASTER.name}</h3>
                    <p className="text-sm font-bold text-indigo-600 uppercase">Владелец проекта</p>
                    <button className="text-xs font-bold text-slate-400 mt-2 hover:text-slate-600">Сменить фото</button>
                </div>
            </div>
            
            <div className="space-y-6">
                <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Название салона</label>
                    <input type="text" defaultValue="Glow Studio Central" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 focus:ring-2 focus:ring-indigo-100 outline-none" />
                </div>
                <div>
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Система налогообложения</label>
                   <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 outline-none">
                       <option>УСН (Доходы)</option>
                       <option>Патент</option>
                   </select>
                </div>
            </div>

            <div className="pt-8 border-t border-slate-100 flex gap-4">
               <button className="bg-indigo-600 px-6 py-3 rounded-xl text-xs font-black text-white uppercase tracking-widest">Сохранить</button>
               <button onClick={onSync} className="bg-slate-100 px-6 py-3 rounded-xl text-xs font-black text-slate-600 uppercase tracking-widest">Синхронизация Google</button>
            </div>
        </GlassCard>
    </div>
)

const StarIcon = ({ fill = "none", size = 16 }: { fill?: string, size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
);
